/**
 * Serviço para buscar detalhes da home/dashboard
 * Integração com API de home-details
 */

import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';

export interface HomeDetails {
  qtdRevendedora: number;
  qtdCoordenadoras: number;
  qtdClientes: number;
  totalAmountSales: number;
  totalAmountSalesSituations: number;
}

export interface ActivityStatus {
  activeRevendedoras: number;
  activeClientes: number;
  inactiveClientes: number;
  inactiveRevendedoras: number;
  totalRevendedoras: number;
  totalClientes: number;
}

export interface SalesLast30DaysResponse {
  currentPeriod: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  previousPeriod: number;
  previousPeriodStart: string;
  previousPeriodEnd: string;
  growth: number;
  growthPercentage: number;
  status: 'crescimento' | 'queda' | 'estável';
}

export interface SalesByMonthResponse {
  month: number;
  monthName: string;
  year: number;
  totalSales: number;
  quantity: number;
  averagePerSale: number;
}

class HomeDetailsService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  private getHeaders(useMatriz: boolean = false): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-api-key': btoa(useMatriz ? 'matriz' : this.subdomain),
    };
  }

  /**
   * Busca os detalhes da home/dashboard
   */
  async findAll(): Promise<HomeDetails> {
    try {
      const response = await httpInterceptor.get('/home-details', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar home details:', error);
      throw error;
    }
  }

  /**
   * Busca o status de atividade de revendedoras e clientes
   */
  async getActivityStatus(): Promise<ActivityStatus> {
    try {
      const response = await httpInterceptor.get('/home-details/activity-status', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar activity status:', error);
      throw error;
    }
  }

  /**
   * Busca vendas dos últimos 30 dias com comparativo
   */
  async getSalesLast30Days(): Promise<SalesLast30DaysResponse> {
    try {
      const response = await httpInterceptor.get('/home-details/sales-last-30-days', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar sales last 30 days:', error);
      throw error;
    }
  }

  /**
   * Busca vendas por mês
   */
  async getSalesByMonth(month?: number, year?: number): Promise<SalesByMonthResponse> {
    try {
      let url = '/home-details/sales-by-month';
      const params = new URLSearchParams();
      
      if (month !== undefined) {
        params.append('month', month.toString());
      }
      if (year !== undefined) {
        params.append('year', year.toString());
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await httpInterceptor.get(url, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar sales by month:', error);
      throw error;
    }
  }
}

export const homeDetailsService = new HomeDetailsService();

/**
 * Formata valor como moeda BRL
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
